import { api, apiGost } from './client'
import { raw } from './http'
import type {
  Campaign,
  JavniUpit,
  JedinicaPravila,
  JedinicaPravilaBody,
  Jezik,
  Katalog,
  MojDan,
  Ponuda,
  PonudaBody,
  PonudaDetalj,
  PonudaKategorija,
  PonudaKategorijaBody,
  PonudaPrijevod,
  SekcijaSirovo,
  Resurs,
  ResursBody,
  ResursiPonude,
  Rezervacija,
  RezervacijaBody,
  RezervacijeOpseg,
  Slotovi,
  Upiti,
  UpitiIshod,
  UpitiPogled,
  UpitOdgovorBody,
} from './types'

/**
 * KATALOG PONUDA (Z22a) — dvije strane, dvije adrese.
 *
 * `ponudeApi` je EDITOR (`/ponude/*`): ista publika kao kampanje — recepcija,
 * administrator i direktor. `katalogApi` je GOST (`/me/ponude`): čita se, ne
 * naručuje, jer u fazi A radi samo izlog-mehanika.
 *
 * Obje su iza modula `rezervacije`: resort koji ga nema dobiva 403
 * `modul_not_enabled` na svakoj od njih, a sučelje ih zato ni ne nudi
 * (`app/moduli.tsx`) — ekran koji vodi u 403 nije zaštita nego zamka.
 */
export const ponudeApi = {
  /** RADNI STOL: sve ponude resorta, i ugašene — ugašenu treba naći da bi se upalila. */
  list: () => api.get<Ponuda[]>('/ponude'),

  get: (id: string) => api.get<Ponuda>(`/ponude/${id}`),

  create: (tijelo: PonudaBody) => api.post<Ponuda>('/ponude', tijelo),

  /** PUNA zamjena sadržaja, odlomaka, kategorija I albuma (CLAUDE.md §16a). */
  update: (id: string, tijelo: PonudaBody) => api.put<Ponuda>(`/ponude/${id}`, tijelo),

  /**
   * GASI, ne briše: ponuda stoji na kampanji koja ju je najavila.
   *
   * **Fizičkog brisanja u sučelju NEMA, i to je svjesna odluka**
   * (dotjerivanje-3 §10): iza ponude stoje rezervacije, a povijest se ne
   * prepisuje zato što je resort preuredio katalog.
   */
  ugasi: (id: string) => api.del(`/ponude/${id}`),

  /** Natrag u katalog (dotjerivanje-3 §10) — suprotan smjer od gašenja. */
  upali: (id: string) => api.post<Ponuda>(`/ponude/${id}/upali`, {}),

  /**
   * "NAJAVI U DOGAĐANJIMA" (dotjerivanje-1 §2) — iz ponude nastaje NACRT
   * kampanje, predispunjen nazivom, sažetkom i albumom, i vezan natrag na ovu
   * stavku ("Rezerviraj" u vitrini).
   *
   * **Ništa se ne objavljuje**: poziv vraća nacrt, a korisnik završava u editoru
   * kampanje, gdje se slažu publika i termin — to su odluke koje ponuda ne zna.
   */
  najavi: (id: string) => api.post<Campaign>(`/ponude/${id}/najavi`, {}),

  /**
   * STROJNA POMOĆ pri prevođenju (dotjerivanje-1 §1) — isti mišić koji kampanja
   * ima od Z18.2 §1, i namjerno isti: dva različita gumba za istu radnju bila bi
   * dva ponašanja koja se prvom izmjenom raziđu.
   *
   * Naziv, opis i SVI odlomci idu jednim pozivom: to je jedan oglas, pa servis
   * koji ga vidi u komadu drži isti ton kroz cijeli detalj. Ništa se ne sprema —
   * nacrt pada u prazna polja koja ostaju editabilna.
   */
  prevedi: (
    jezikIzvornika: Jezik,
    naziv: string | null,
    opis: string | null,
    sekcije: SekcijaSirovo[],
  ) =>
    api.post<{ prijevodi: PonudaPrijevod[] }>('/ponude/prevedi', {
      jezik_izvornika: jezikIzvornika,
      naziv,
      opis,
      sekcije,
    }),

  kategorije: () => api.get<PonudaKategorija[]>('/ponude/kategorije'),

  /**
   * Pružateljske jedinice. Vlastita adresa, jer ovaj modul čita org-stablo iz TRI
   * uloge, a `/admin/objects` i `/reception/objects` svaka vidi drugu.
   */
  pruzatelji: () => api.get<{ objekt_id: string; naziv: string }[]>('/ponude/pruzatelji'),

  createKategorija: (tijelo: PonudaKategorijaBody) =>
    api.post<PonudaKategorija>('/ponude/kategorije', tijelo),

  updateKategorija: (id: string, tijelo: PonudaKategorijaBody) =>
    api.put<PonudaKategorija>(`/ponude/kategorije/${id}`, tijelo),

  ugasiKategoriju: (id: string) => api.del(`/ponude/kategorije/${id}`),
}

/**
 * Katalog gosta. Chipovi i popis stižu ZAJEDNO — filtar se crta iznad popisa i
 * mora doći s njim, inače popis jedan tren stoji bez njega pa poskoči.
 *
 * Filtar je parametar, a ne druga adresa: chip je pogled na isti popis. Nepoznat
 * ili ugašen id daje prazan popis, ne grešku.
 */
export const katalogApi = {
  katalog: (kategorijaId?: string | null) =>
    apiGost.get<Katalog>(`/me/ponude${kategorijaId ? `?kategorija=${kategorijaId}` : ''}`),

  detalj: (id: string) => apiGost.get<PonudaDetalj>(`/me/ponude/${id}`),
}

/**
 * RESURSI I PRAVILA JEDINICE (Z22b §7) — editor motora termina.
 *
 * Stoji uz `ponudeApi`, na istoj adresi i iza istog modula: raspored se slaže
 * dok se slaže katalog. Veza ponuda↔resurs ovdje NEMA — ona je dio obrasca
 * ponude i putuje njezinim `PUT`-om, jer je to jedno "spremi" (CLAUDE.md §16a).
 */
export const resursiApi = {
  /** RADNI STOL: svi, i ugašeni — ugašenog treba naći da bi se upalio. */
  list: () => api.get<Resurs[]>('/ponude/resursi'),

  get: (id: string) => api.get<Resurs>(`/ponude/resursi/${id}`),

  create: (tijelo: ResursBody) => api.post<Resurs>('/ponude/resursi', tijelo),

  /** PUNA zamjena sadržaja, radnog tjedna I iznimaka. */
  update: (id: string, tijelo: ResursBody) => api.put<Resurs>(`/ponude/resursi/${id}`, tijelo),

  /** GASI, ne briše — a ni to dok drži budući termin (409 `resurs_in_use`). */
  ugasi: (id: string) => api.del(`/ponude/resursi/${id}`),

  pravila: (objektId: string) => api.get<JedinicaPravila>(`/ponude/jedinice/${objektId}/pravila`),

  postaviPravila: (objektId: string, tijelo: JedinicaPravilaBody) =>
    api.put<JedinicaPravila>(`/ponude/jedinice/${objektId}/pravila`, tijelo),
}

/**
 * TOK TERMINA I MOJE REZERVACIJE (Z22b §6) — gostova strana motora.
 *
 * Tri koraka, tri adrese, jer su to tri pitanja: kod koga (jednom), kad (pri
 * svakom listanju kalendara) i "uzmi" (jednom, i to je transakcija).
 */
export const rezervacijeApi = {
  resursi: (ponudaId: string) => apiGost.get<ResursiPonude>(`/me/ponude/${ponudaId}/resursi`),

  /** Datum je OBAVEZAN: kalendar pita dan po dan (prazan popis je pošten odgovor). */
  slotovi: (ponudaId: string, datum: string, resursId?: string | null) =>
    apiGost.get<Slotovi>(
      `/me/ponude/${ponudaId}/slotovi?datum=${datum}${resursId ? `&resurs=${resursId}` : ''}`,
    ),

  moje: (opseg: RezervacijeOpseg = 'nadolazece') =>
    apiGost.get<Rezervacija[]>(`/me/rezervacije?opseg=${opseg}`),

  detalj: (id: string) => apiGost.get<Rezervacija>(`/me/rezervacije/${id}`),

  rezerviraj: (tijelo: RezervacijaBody) => apiGost.post<Rezervacija>('/me/rezervacije', tijelo),

  /**
   * Otkaz je vlastita adresa, a ne `DELETE`: rezervacija ne nestaje nego mijenja
   * stanje, i to stanje gost i dalje vidi u popisu ("otkazano").
   *
   * Od Z22c isti poziv ODBIJA protuprijedlog — to je isti ishod, pa nema druge
   * adrese (poslužitelj piše `otkazana`).
   */
  otkazi: (id: string, razlog?: string | null) =>
    apiGost.post<Rezervacija>(`/me/rezervacije/${id}/otkazi`, { razlog: razlog ?? null }),

  /**
   * POVLAČENJE UPITA (Z22c §A) — dok nitko nije odgovorio. Vlastita adresa, a ne
   * otkaz: dogovora još nema, pa pružatelj mora vidjeti da ga nitko ne čeka.
   */
  povuci: (id: string, razlog?: string | null) =>
    apiGost.post<Rezervacija>(`/me/rezervacije/${id}/povuci`, { razlog: razlog ?? null }),

  /** PRIHVAT PROTUPRIJEDLOGA (Z22c §A): predloženi termin postaje termin. */
  prihvati: (id: string) => apiGost.post<Rezervacija>(`/me/rezervacije/${id}/prihvati`, {}),
}

/**
 * RED UPITA (Z22c §A) — PRUŽATELJEVA strana, na površini zaposlenika.
 *
 * Staff klijentom (`api`), kao i "Moj dan": ovo gleda čovjek koji je prijavljen u
 * resort, a ne gost. Publiku izvodi poslužitelj iz org-stabla — ekran ne šalje
 * "čija jedinica" i ne bi ni smio.
 */
export const upitiApi = {
  /**
   * TRI PRETINCA (dotjerivanje-4 §4): novi, čeka gosta, riješeni — jedan popis,
   * tri pogleda. `ishod` sužava samo riješene i poslužitelj ga drugdje ignorira,
   * pa se šalje uvijek: dva oblika adrese značila bi dva mjesta koja moraju znati
   * isto pravilo.
   */
  red: (pogled: UpitiPogled = 'novi', ishod: UpitiIshod = 'svi') =>
    api.get<Upiti>(`/staff/upiti?pogled=${pogled}&ishod=${ishod}`),

  potvrdi: (id: string) => api.post<Rezervacija>(`/staff/upiti/${id}/potvrdi`, {}),

  odbij: (id: string, poruka?: string | null) =>
    api.post<Rezervacija>(`/staff/upiti/${id}/odbij`, { poruka: poruka ?? null }),

  /** Protuprijedlog BEZ termina poslužitelj odbija — obrazac ga zato traži. */
  predlozi: (id: string, tijelo: UpitOdgovorBody) =>
    api.post<Rezervacija>(`/staff/upiti/${id}/protuprijedlog`, tijelo),
}

/**
 * JAVNA STRANICA IZ E-POŠTE (Z22c §A) — pružatelj koji aplikaciju nema.
 *
 * **Bez ijedne sesije**, pa ide golim `raw`-om, a ne `api`/`apiGost` klijentom:
 * dokaz je TOKEN iz adrese, i drugog nema. Klijent koji bi ovamo prilijepio tuđi
 * `Authorization` slao bi tuđu sesiju na anonimnu površinu.
 */
export const javniUpitApi = {
  detalj: (token: string) => raw<JavniUpit>(`/auth/upit/${encodeURIComponent(token)}`),

  odgovori: (token: string, akcija: 'potvrdi' | 'odbij' | 'protuprijedlog', tijelo?: UpitOdgovorBody) =>
    raw<JavniUpit>(`/auth/upit/${encodeURIComponent(token)}/${akcija}`, {
      metoda: 'POST',
      tijelo: tijelo ?? {},
    }),
}

/** "MOJ DAN" (Z22b §7) — pružateljeva strana, na površini zaposlenika. */
export const mojDanApi = {
  dan: (datum?: string | null) => api.get<MojDan>(`/staff/moj-dan${datum ? `?datum=${datum}` : ''}`),
}
